import { useState, useCallback, useRef } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { supabase, patchProfile } from "../../../lib/supabase";

interface AvatarUploaderProps {
  size?: "sm" | "md" | "lg";
}

export default function AvatarUploader({ size = "md" }: AvatarUploaderProps) {
  const { user, profile, refreshProfile } = useAuth();
  const { isDark } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const nameHint = user?.email?.split("@")[0] ?? "?";
  const initial = nameHint.charAt(0).toUpperCase();
  const avatarSrc = localUrl ?? profile?.avatar_url ?? null;

  const isPro = profile?.plan === "pro";

  const sizeClass =
    size === "lg" ? "w-24 h-24 text-3xl"
    : size === "sm" ? "w-12 h-12 text-base"
    : "w-20 h-20 text-2xl";

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 5000);
  };

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file.", false);
      return;
    }

    // Compress + crop to square
    const compress = (f: File): Promise<Blob> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        const blobUrl = URL.createObjectURL(f);
        img.onload = () => {
          const dim = 256;
          const canvas = document.createElement("canvas");
          canvas.width = dim;
          canvas.height = dim;
          const ctx = canvas.getContext("2d");
          if (!ctx) { reject(new Error("Canvas not available")); return; }
          const min = Math.min(img.naturalWidth, img.naturalHeight);
          const sx = (img.naturalWidth - min) / 2;
          const sy = (img.naturalHeight - min) / 2;
          ctx.drawImage(img, sx, sy, min, min, 0, 0, dim, dim);
          canvas.toBlob((b) => {
            URL.revokeObjectURL(blobUrl);
            if (b) resolve(b);
            else reject(new Error("Image conversion failed"));
          }, "image/jpeg", 0.85);
        };
        img.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          reject(new Error("Could not load image"));
        };
        img.src = blobUrl;
      });

    setUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showToast("Session expired — sign out and back in.", false);
        return;
      }

      const blob = await compress(file);
      const path = `${user.id}/avatar.jpg`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, blob, { upsert: true, contentType: "image/jpeg" });

      if (uploadError) throw new Error(`Storage: ${uploadError.message}`);

      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      // Use direct REST call with explicit access token
      await patchProfile(session.access_token, user.id, { avatar_url: publicUrl });

      setLocalUrl(publicUrl);
      await refreshProfile();
      showToast("Profile picture updated!", true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(msg || "Upload failed. Try again.", false);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [user, refreshProfile]);

  const isError = toast && !toast.ok;

  return (
    <div className="relative flex-shrink-0">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className={`relative ${sizeClass} rounded-full overflow-hidden cursor-pointer group focus:outline-none`}
        title="Change profile picture"
      >
        {avatarSrc ? (
          <img src={avatarSrc} alt="Profile" className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center font-syne font-bold select-none ${isDark ? "bg-[#1a1a1a] text-[#555]" : "bg-[#f0f0f0] text-[#aaa]"}`}>
            {initial}
          </div>
        )}
        <div className={`absolute inset-0 flex flex-col items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${isDark ? "bg-black/65" : "bg-black/40"}`}>
          {uploading
            ? <i className="ri-loader-4-line text-white text-lg animate-spin" />
            : <>
                <i className="ri-camera-line text-white text-base" />
                <span className="text-white text-[9px] font-inter font-medium">Change</span>
              </>}
        </div>
      </button>

      {/* Ring — amber when Pro, subtle otherwise */}
      <div className={`absolute inset-0 rounded-full ring-2 pointer-events-none transition-all duration-300 ${
        isPro
          ? "ring-amber-400/60"
          : isDark ? "ring-white/10" : "ring-black/10"
      }`} />

      {/* Pro crown badge */}
      {isPro && (
        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center z-10 ring-2 bg-amber-500 ${isDark ? "ring-[#050505]" : "ring-white"}`}>
          <i className="ri-vip-crown-fill text-[10px] text-black" />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-inter font-medium px-3 py-1.5 rounded-full z-20 ${
          isError
            ? isDark ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-red-50 text-red-600 border border-red-200"
            : isDark ? "bg-white/10 text-white border border-white/15" : "bg-[#111]/90 text-white"
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
